import {
  collection, doc, onSnapshot, query, orderBy, where, getDocs, writeBatch, serverTimestamp, arrayUnion, arrayRemove,
} from "firebase/firestore";
import { db } from "../../firebase";
import { STATEMENT_KINDS } from "./statementKinds";
import { TASK_LINKS } from "../tasks/taskLinks";
import { unlinkTasks } from "../tasks/taskLinkService";

const statementsRef = (uid, kind) => collection(db, "users", uid, STATEMENT_KINDS[kind].collection);
const statementRef = (uid, kind, id) => doc(db, "users", uid, STATEMENT_KINDS[kind].collection, id);
const goalRef = (uid, goalId) => doc(db, "users", uid, "goals", goalId);

// Live list of one kind, oldest first. Returns the unsubscribe function for effect cleanup.
export const subscribeStatements = (uid, kind, onItems, onError) =>
  onSnapshot(query(statementsRef(uid, kind), orderBy("createdAt", "asc")),
    (snap) => onItems(snap.docs.map(d => ({ id: d.id, ...d.data() }))), onError);

// Adds the link on the goal side for each goal gained, and takes it off each goal lost. Updates, never
// merges: a merge write to a goal deleted elsewhere would bring it back as an empty goal, where an
// update fails the batch instead. Goals this device knows are gone are skipped so the common case works.
const writeGoalSide = (batch, uid, kind, statementId, gained, lost, existingGoalIds) => {
  const field = STATEMENT_KINDS[kind].goalField;
  const exists = (id) => existingGoalIds.has(id);
  gained.filter(exists).forEach(goalId => batch.update(goalRef(uid, goalId), { [field]: arrayUnion(statementId), updatedAt: serverTimestamp() }));
  lost.filter(exists).forEach(goalId => batch.update(goalRef(uid, goalId), { [field]: arrayRemove(statementId), updatedAt: serverTimestamp() }));
};

// The statement's own fields. Its goalIds keep only goals that exist, so both sides of a link agree.
const statementFields = (kind, { text, categoryId, categoryLabel, goalIds }, existingGoalIds) => {
  const k = STATEMENT_KINDS[kind];
  return {
    [k.textField]: text, categoryId: categoryId || null,
    ...(k.labelField ? { [k.labelField]: categoryLabel || "" } : {}),
    goalIds: [...goalIds].filter(id => existingGoalIds.has(id)),
  };
};

// A new statement and its goal links, in one batch. Resolves to the new id. `existingGoalIds` is the
// Set of the user's goal ids.
export const createStatement = (uid, kind, fields, existingGoalIds) => {
  const batch = writeBatch(db);
  const ref = doc(statementsRef(uid, kind));
  batch.set(ref, { ...statementFields(kind, fields, existingGoalIds), createdAt: serverTimestamp(), updatedAt: serverTimestamp() });
  writeGoalSide(batch, uid, kind, ref.id, [...fields.goalIds], [], existingGoalIds);
  return batch.commit().then(() => ref.id);
};

// Saves an edit. Only goals whose link changed are written on the goal side. An update, so a statement
// deleted elsewhere fails to save instead of coming back without its createdAt (and so off the page).
export const updateStatement = (uid, kind, statement, fields, existingGoalIds) => {
  const before = new Set(statement.goalIds || []);
  const own = statementFields(kind, fields, existingGoalIds);
  const after = new Set(own.goalIds);
  const batch = writeBatch(db);
  batch.update(statementRef(uid, kind, statement.id), { ...own, updatedAt: serverTimestamp() });
  writeGoalSide(batch, uid, kind, statement.id, [...after].filter(id => !before.has(id)), [...before].filter(id => !after.has(id)), existingGoalIds);
  return batch.commit();
};

// Deletes a statement and takes it off every goal it was linked to. A fear's tasks are kept, no longer tied to it.
export const deleteStatement = async (uid, kind, statement, existingGoalIds) => {
  const batch = writeBatch(db);
  batch.delete(statementRef(uid, kind, statement.id));
  writeGoalSide(batch, uid, kind, statement.id, [], statement.goalIds || [], existingGoalIds);
  if (TASK_LINKS[kind]) await unlinkTasks(batch, uid, TASK_LINKS[kind], statement.id);
  return batch.commit();
};

// The new order of one group, in one batch. Writes only the statements whose position changed.
export const writeStatementOrder = (uid, kind, ordered) => {
  const batch = writeBatch(db);
  let changed = 0;
  ordered.forEach((s, i) => {
    if (s.sortOrder === i) return;
    batch.update(statementRef(uid, kind, s.id), { sortOrder: i, updatedAt: serverTimestamp() });
    changed += 1;
  });
  return changed ? batch.commit() : Promise.resolve();
};

// When a goal is deleted, take it off every vision statement and fear that links to it, in the
// caller's batch. Found from the statements' side, so a statement whose link the goal lost track of
// is still cleaned, and a deleted statement is never written back.
export const unlinkDeletedGoal = async (batch, uid, goalId) => {
  const kinds = Object.keys(STATEMENT_KINDS);
  const snaps = await Promise.all(kinds.map(kind =>
    getDocs(query(statementsRef(uid, kind), where("goalIds", "array-contains", goalId)))));
  snaps.forEach((snap, i) => snap.docs.forEach(d =>
    batch.update(statementRef(uid, kinds[i], d.id), { goalIds: arrayRemove(goalId), updatedAt: serverTimestamp() })));
};
